import type { AppliedDiscount, CartEvaluationResult, EvaluatedCartLine } from '../domain/promotionTypes';
import { formatMoney } from '../../shared/format/public-api';
import { PROMOTION_SCOPE_LABELS } from './promotionPresentation';
import { formatQtyCompact } from '@shared/quantity';

export const formatPromoMoney = formatMoney;

export function buildEvaluatedLineMap(
  evaluation: CartEvaluationResult | null | undefined
): Map<string, EvaluatedCartLine> {
  if (!evaluation) return new Map();
  return new Map(evaluation.lines.map((line) => [line.lineId, line]));
}

export type PosLinePriceBlock = {
  listLabel: string;
  netLabel: string;
  hasDiscount: boolean;
};

export function formatPosLinePriceBlock(
  line: EvaluatedCartLine | undefined,
  fallbackGross: number
): PosLinePriceBlock {
  if (!line || line.lineDiscountTotal <= 0) {
    const label = formatPromoMoney(fallbackGross);
    return { listLabel: label, netLabel: label, hasDiscount: false };
  }
  const gross = line.unitPriceOriginal * line.quantity;
  return {
    listLabel: formatPromoMoney(gross),
    netLabel: formatPromoMoney(line.lineSubtotal),
    hasDiscount: true,
  };
}

function formatSingleDiscountHint(discount: AppliedDiscount): string {
  const amountLabel = `−${formatPromoMoney(discount.discountAmount)}`;

  switch (discount.kind) {
    case 'percent_off':
      return discount.scope === 'cart' ? `Descuento del carrito · ${amountLabel}` : amountLabel;
    case 'fixed_off': {
      if (discount.scope !== 'cart') return amountLabel;
      const quantityAffected = discount.quantityAffected != null && discount.quantityAffected > 0
        ? ` · ${formatQtyCompact(discount.quantityAffected, 'unit')} ${
            discount.quantityAffected === 1 ? 'unidad' : 'unidades'
          } del combo`
        : '';
      return `Parte del descuento${quantityAffected} · ${amountLabel}`;
    }
    case 'combo_buy_x_get_y': {
      const freePart =
        discount.quantityAffected != null && discount.quantityAffected > 0
          ? ` · ${discount.quantityAffected} gratis`
          : '';
      return `${discount.promotionName}${freePart}`;
    }
    case 'combo_n_for_price':
      return `${discount.promotionName} · ${amountLabel}`;
    default:
      return `${discount.promotionName} · ${amountLabel}`;
  }
}

export function formatPosLinePromoHint(discounts: AppliedDiscount[]): string {
  if (discounts.length === 0) return '';
  if (discounts.length === 1) {
    return formatSingleDiscountHint(discounts[0]!);
  }
  const total = discounts.reduce((sum, d) => sum + d.discountAmount, 0);
  return `${discounts.length} promos · −${formatPromoMoney(total).slice(1)}`;
}

export function formatPosLinePromoAccessibilityLabel(discounts: AppliedDiscount[]): string {
  if (discounts.length === 0) return '';
  return discounts.map((d) => `${d.promotionName} ${formatPromoMoney(d.discountAmount)}`).join(', ');
}

export type PosLineDiscountBadge = {
  label: string;
  accessibilityLabel: string;
};

/** Badge compacto para filas de carrito (sin texto largo de promo). */
export function formatPosLineDiscountBadge(discounts: AppliedDiscount[]): PosLineDiscountBadge | null {
  if (discounts.length === 0) return null;
  const total = discounts.reduce((sum, d) => sum + d.discountAmount, 0);
  const amountLabel = `−${formatPromoMoney(total)}`;
  return {
    label: amountLabel,
    accessibilityLabel: formatPosLinePromoAccessibilityLabel(discounts),
  };
}

export type CheckoutDiscountSummaryRow = {
  promotionId: string;
  promotionName: string;
  discountAmount: number;
  scopeLabel: string;
};

export function summarizeCheckoutDiscounts(evaluation: CartEvaluationResult): CheckoutDiscountSummaryRow[] {
  const rows: CheckoutDiscountSummaryRow[] = [];
  const seen = new Set<string>();

  const push = (discount: AppliedDiscount) => {
    const key = `${discount.promotionId}:${discount.scope}:${discount.lineId ?? ''}:${discount.discountAmount}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({
      promotionId: discount.promotionId,
      promotionName: discount.promotionName,
      discountAmount: discount.discountAmount,
      scopeLabel: PROMOTION_SCOPE_LABELS[discount.scope],
    });
  };

  for (const line of evaluation.lines) {
    for (const discount of line.appliedDiscounts) {
      push(discount);
    }
  }
  for (const discount of evaluation.cartDiscounts) {
    push(discount);
  }
  return rows;
}

export type CheckoutLineSummaryRow = {
  lineId: string;
  quantity: number;
  listTotal: number;
  discountTotal: number;
  netTotal: number;
};

export function summarizeCheckoutLines(
  cart: Array<{ lineId: string; quantity: number; price: number }>,
  evaluation: CartEvaluationResult | null | undefined
): CheckoutLineSummaryRow[] {
  const map = buildEvaluatedLineMap(evaluation);
  return cart.map((item) => {
    const evaluated = map.get(item.lineId);
    const listTotal = evaluated
      ? evaluated.unitPriceOriginal * evaluated.quantity
      : item.price * item.quantity;
    const discountTotal = evaluated?.lineDiscountTotal ?? 0;
    const netTotal = evaluated?.lineSubtotal ?? listTotal;
    return {
      lineId: item.lineId,
      quantity: item.quantity,
      listTotal,
      discountTotal,
      netTotal,
    };
  });
}
